import React, { useEffect, useState } from 'react';

interface WarningPopupProps {
  message: string;
  isVisible: boolean;
  onClose: () => void;
  duration?: number; // Duration in milliseconds before auto-fade
}

const WarningPopup: React.FC<WarningPopupProps> = ({ 
  message, 
  isVisible, 
  onClose, 
  duration = 4000 // Shorter duration for warnings
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [progressWidth, setProgressWidth] = useState(100);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      setProgressWidth(100);
      
      // Start progress bar countdown
      const progressTimer = setInterval(() => {
        setProgressWidth(prev => {
          if (prev <= 0) {
            clearInterval(progressTimer);
            return 0;
          }
          return prev - (100 / (duration / 50)); // Update every 50ms
        });
      }, 50);
      
      // Auto-fade after duration
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTimeout(onClose, 300); // Wait for fade-out animation
      }, duration);

      return () => {
        clearTimeout(timer);
        clearInterval(progressTimer);
      };
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          isAnimating ? 'bg-opacity-20' : 'bg-opacity-0'
        }`}
      />
      
      {/* Popup */}
      <div 
        className={`relative bg-white rounded-xl shadow-2xl px-6 py-4 max-w-sm mx-4 transform transition-all duration-300 pointer-events-auto ${
          isAnimating 
            ? 'scale-100 opacity-100 translate-y-0' 
            : 'scale-95 opacity-0 translate-y-2'
        }`}
      >
        {/* Warning Icon */}
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg 
                className="w-5 h-5 text-yellow-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
                />
              </svg>
            </div>
          </div>
          
          {/* Message */}
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              {message}
            </p>
          </div>
          
          {/* Close Button */}
          <button
            onClick={() => {
              onClose();
            }}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-yellow-500 rounded-full transition-all duration-50 ease-linear"
            style={{
              width: `${progressWidth}%`
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default WarningPopup;

